import type { CategoryTree, County } from '@hemmafixa/shared';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useI18n } from '@/i18n/context';
import { api } from '@/lib/api-client';

/** Service categories from the catalogue (P2-08). They rarely change, so they are fetched once per visit. */
export function useCategories() {
  return useQuery({
    queryKey: ['catalogue', 'categories'],
    queryFn: () => api<CategoryTree>('/categories'),
    staleTime: Infinity,
  });
}

export interface CategoryOption {
  slug: string;
  label: string;
  /** Sub-categories are listed under their main category */
  isMain: boolean;
}

/** Main categories, each followed by its sub-categories, named in the current language. */
export function useCategoryOptions(): CategoryOption[] {
  const { language } = useI18n();
  const { data } = useCategories();
  return useMemo(
    () =>
      (data?.items ?? []).flatMap((main) => [
        { slug: main.slug, label: language === 'sv' ? main.nameSv : main.nameEn, isMain: true },
        ...main.children.map((child) => ({
          slug: child.slug,
          label: language === 'sv' ? child.nameSv : child.nameEn,
          isMain: false,
        })),
      ]),
    [data, language],
  );
}

/** The category's name in the current language; the slug while the catalogue loads or for an unknown slug. */
export function useCategoryLabel() {
  const options = useCategoryOptions();
  const { t } = useI18n();
  const bySlug = useMemo(() => new Map(options.map((option) => [option.slug, option.label])), [options]);
  return useCallback(
    (slug: string) => bySlug.get(slug) ?? (t.categories as Record<string, string>)[slug] ?? slug,
    [bySlug, t],
  );
}

/** The 21 counties (län) for choosing service areas. */
export function useCounties() {
  return useQuery({
    queryKey: ['catalogue', 'counties'],
    queryFn: () => api<County[]>('/counties'),
    staleTime: Infinity,
  });
}
